// src/pages/RRTS/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import GeneralSettings from "../../Components/Common/GeneralSettings";
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import ActionButton from "../../Components/Common/ActionButton";

// Page-specific Components
import GroupManager from "./components/GroupManager";

// API
import {
  getRRTSData,
  generateRRTSTable,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useJobPolling } from "../../hooks/useJobPolling";
import { useGroupsManager } from "../../hooks/useGroupsManager";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Config
import {
  rrtsConfig,
  rrtsDefaults,
  rrtsValidation,
} from "../../config/plotConfigs/rrtsConfig";

// Short code handling
import {
  useRRTSShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";
import InfoTooltip from "../../Components/Common/InfoTooltip";

const RRTS = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(true);
  const [plotData, setPlotData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  // Analysis mode state (separate from settings)
  const [analysisMode, setAnalysisMode] = useState("single");

  const [apiData, setApiData] = useState({
    json_files: [],
    organism: "",
    transcriptome: "",
  });

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(rrtsDefaults, rrtsValidation);

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const { plotLoading, taskId, startJob } = useJobPolling();

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
    userModifiedFields,
  } = useSmartReadLength();

  // Use the shared groups manager hook
  const {
    groups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    clearAllGroups,
    handleAutoFill,
    handleAutoLabel,
    resetGroups,
  } = useGroupsManager([
    { id: "group1", name: "Group 1", files: [] },
    { id: "group2", name: "Group 2", files: [] },
  ]);

  // Use the RRTS specific short code handler
  const {
    autoRunPlot: autoRunAnalysis,
    setAutoRunPlot: setAutoRunAnalysis,
    consumeUserPassedShortCode
  } = useRRTSShortCode({
    location,
    updateMultipleSettings,
    setSelectedFiles,
    resetGroups,
    setAnalysisMode,
  });

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunAnalysis(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Load data on component mount
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await getRRTSData(projectId);
        const data = response?.data || response || {};

        setApiData({
          json_files: data.json_files || [],
          organism: data.organism || "",
          transcriptome: data.transcriptome || "",
        });

        // Set default seq type
        const fileTypes = data.json_files
          ? [...new Set(data.json_files.map((x) => x.file_type))]
          : [];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        // Set default group
        const uniqueGroups = [
          ...new Set(
            data.json_files?.map((file) => file.subgroup).filter(Boolean) || []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Failed to load project data:", err);
        const errorMsg = "Failed to load project data";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger analysis generation when autoRunAnalysis is true
  useEffect(() => {
    if (
      autoRunAnalysis &&
      !plotLoading &&
      !loading &&
      ((analysisMode === "single" && selectedFiles.length > 0) ||
        (analysisMode === "comparison" &&
          groups.some((g) => g.files.length > 0)))
    ) {
      console.log("Auto-triggering analysis generation from short code...");
      // Set flag to false immediately to prevent multiple calls
      setAutoRunAnalysis(false);
      // Small delay to ensure all settings are loaded
      setTimeout(() => {
        handleGenerate();
      }, 50);
    }
  }, [
    autoRunAnalysis,
    plotLoading,
    loading,
    selectedFiles.length,
    groups.map((g) => g.files.length).join(","), // Only track file counts, not full arrays
    analysisMode,
    setAutoRunAnalysis,
  ]);

  // Smart read length adjustment
  useEffect(() => {
    // Determine which file ids to use depending on mode
    let fileIdsForAdjustment = [];

    if (analysisMode === "single") {
      fileIdsForAdjustment = selectedFiles;
    } else {
      fileIdsForAdjustment = groups
        .flatMap((g) => g.files || [])
        .filter(Boolean);
      // dedupe
      fileIdsForAdjustment = Array.from(new Set(fileIdsForAdjustment));
    }

    // Only attempt when we have project file metadata loaded
    if (!apiData.json_files?.length || fileIdsForAdjustment.length === 0)
      return;

    // Use the correct setting keys for RRTS (minread/maxread)
    if (
      shouldAutoAdjust(
        "minread",
        settings.minReadLength,
        settings.maxReadLength
      ) &&
      shouldAutoAdjust(
        "maxread",
        settings.minReadLength,
        settings.maxReadLength
      )
    ) {
      const { min, max } = getStandardReadLengths(
        fileIdsForAdjustment,
        apiData.json_files
      );

      // Ensure comparison uses string/number-safe equality
      if (
        String(settings.minReadLength) !== String(min) ||
        String(settings.maxReadLength) !== String(max)
      ) {
        updateMultipleSettings({
          minReadLength: min,
          maxReadLength: max,
        });
        toast.info(`Read lengths auto-adjusted: Min=${min}, Max=${max}`, {
          autoClose: 3000,
        });
      }
    }
  }, [
    selectedFiles,
    groups,
    analysisMode,
    apiData.json_files,
    getStandardReadLengths,
    shouldAutoAdjust,
    updateMultipleSettings,
    settings.minread,
    settings.maxread,
  ]);

  // Reset selections when analysis mode changes
  useEffect(() => {
    if (analysisMode === "single") {
      clearAllGroups();
    } else {
      clearSelection();
    }
  }, [analysisMode, clearSelection, clearAllGroups]);

  // Computed values
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    let files = apiData.json_files;
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((x) => x.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((x) => x.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const types = [...new Set(apiData.json_files.map((x) => x.file_type))];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count: apiData.json_files.filter((x) => x.file_type === type).length,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const groupSet = new Set(
      apiData.json_files.map((x) => x.subgroup).filter(Boolean)
    );
    return Array.from(groupSet).map((group) => ({
      value: group,
      label: group,
      count: apiData.json_files.filter((x) => x.subgroup === group).length,
    }));
  }, [apiData.json_files]);

  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    // Use all files, not filtered files, and filter only by seq type
    const seqTypeFiles = (apiData.json_files || []).filter(
      (x) => x.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedSeqType, apiData.json_files, selectedFiles]);

  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (x) => x.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Event handlers
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      // Mark read length fields as user modified when manually changed
      if (key === "minReadLength" || key === "maxReadLength") {
        markFieldAsUserModified(key);
      }
    },
    [updateSetting, markFieldAsUserModified]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    clearSelection();
    setAnalysisMode("single");
    resetGroups([
      { id: "group1", name: "Group 1", files: [] },
      { id: "group2", name: "Group 2", files: [] },
    ]);
    setPlotData(null);
    setTableData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetUserModifications, clearSelection, resetGroups]);

  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      // Use all files, not filtered files, and filter only by seq type
      const seqTypeFiles = (apiData.json_files || []).filter(
        (x) => x.file_type === selectedSeqType
      );
      const fileIds = seqTypeFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [
      selectedSeqType,
      apiData.json_files,
      selectMultipleFiles,
      deselectMultipleFiles,
    ]
  );

  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (x) => x.subgroup === selectedGroup
      );
      const fileIds = groupFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const buildPayload = useCallback(() => {
    // Use the RRTS specific payload creator
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    return createPlotSpecificPayload.rrts({
      settings,
      selectedFiles,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
      groups,
      analysisMode,
      apiData,
    });
  }, [
    settings,
    selectedFiles,
    groups,
    analysisMode,
    projectId,
    apiData,
    consumeUserPassedShortCode,
  ]);

  // Generate analysis with enhanced validation
  const handleGenerate = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Enhanced validation for different modes
    if (analysisMode === "comparison") {
      const group1Files = groups[0]?.files?.length || 0;
      const group2Files = groups[1]?.files?.length || 0;

      if (group1Files === 0 || group2Files === 0) {
        toast.error("Please put files in both groups for comparison analysis");
        return;
      }

      if (!groups[0]?.name?.trim() || !groups[1]?.name?.trim()) {
        toast.error("Please provide names for both groups");
        return;
      }
    } else if (selectedFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    const payload = buildPayload();
    console.log("Submitting RRTS payload:", payload);

    const onSuccess = (result) => {
      setTableData(result);
      setError(null);
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setTableData(null);
    };

    startJob(generateRRTSTable, payload, onSuccess, onError);
  }, [
    plotLoading,
    validateSettings,
    analysisMode,
    groups,
    selectedFiles.length,
    buildPayload,
    startJob,
  ]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "RRTS Analysis | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "RRTS Analysis" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  RRTS Analysis
                </h4>
                <div>
                  {" "}
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={handleReset}
                    disabled={plotLoading}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Reset All
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Settings Row */}
          <Row className="mb-4">
            {/* Left: RRTS Settings */}
            <Col lg={12}>
              <GeneralSettings
                config={rrtsConfig}
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                title="RRTS Analysis Settings"
                // Pass analysis mode to conditionally show comparison-specific fields
                additionalProps={{ analysisMode }}
                analysisMode={analysisMode}
                onSetAnalysisMode={setAnalysisMode}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right: Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={analysisMode == "single"}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Conditional File Selection or Group Management */}
          {analysisMode === "single" ? (
            <Row className="mb-4">
              <Col lg={12}>
                <Files
                  files={filteredFiles}
                  selectedFiles={selectedFiles}
                  onToggleFile={toggleFile}
                  onSelectAllGroupFiles={handleSelectAllGroupFiles}
                  // onToggleAll={toggleAllFiles}
                  title="Files"
                />
              </Col>
            </Row>
          ) : (
            <Row className="mb-4">
              <Col lg={12}>
                <GroupManager
                  groups={groups}
                  allFiles={apiData.json_files}
                  filteredFiles={filteredFiles}
                  onAddFilesToGroup={addFilesToGroup}
                  onRemoveFilesFromGroup={removeFilesFromGroup}
                  onUpdateGroupName={updateGroupName}
                  onClearGroup={clearGroup}
                  onAutoFill={handleAutoFill}
                  onAutoLabel={handleAutoLabel}
                  onClearAll={clearAllGroups}
                  groupLabels={{
                    group1: groups[0]?.name || "Group 1",
                    group2: groups[1]?.name || "Group 2",
                  }}
                  onUpdateGroupLabel={(groupId, name) =>
                    updateGroupName(groupId, name)
                  }
                />
              </Col>
            </Row>
          )}

          {/* Selected Files Display */}
          <Row>
            <Col lg={12}>
              <SelectedFilesDisplay
                selectedFiles={selectedFiles}
                allFiles={apiData.json_files}
                title="Selected Files for RRTS"
                showSearch={true}
                maxDisplayFiles={0}
              />
            </Col>
          </Row>

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div ref={loadingDivRef}  
                   className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  {analysisMode === "single" ? (
                    <span>
                      <i className="ri-file-list-3-line me-1"></i>
                      Selected Files: <strong>{selectedFiles.length}</strong>
                      {selectedFiles.length === 0 && (
                        <span className="text-warning ms-2">
                          Please select at least one file
                        </span>
                      )}
                      {selectedFiles.length > 0 && (
                        <i className="ri-check-circle-line ms-2 text-success"></i>
                      )}
                    </span>
                  ) : (
                    <span>
                      <i className="ri-group-line me-1"></i>
                      {groups[0]?.name || "Group 1"}:{" "}
                      <strong>{groups[0]?.files?.length || 0}</strong> files |{" "}
                      {groups[1]?.name || "Group 2"}:{" "}
                      <strong>{groups[1]?.files?.length || 0}</strong> files{" "}
                      {(groups[0]?.files?.length || 0) === 0 ||
                      (groups[1]?.files?.length || 0) === 0 ? (
                        <span className="text-warning ms-2">
                          Both groups need files for comparison
                        </span>
                      ) : (
                        <i className="ri-check-circle-line ms-2 text-success"></i>
                      )}
                    </span>
                  )}
                  {taskId && (
                    <span className="ms-2">
                      - <strong>Task ID:</strong> {taskId}
                    </span>
                  )}
                </div>

                <ActionButton
                  onClick={handleGenerate}
                  loading={plotLoading}
                  disabled={
                    (analysisMode === "single" && selectedFiles.length === 0) ||
                    (analysisMode === "comparison" &&
                      ((groups[0]?.files?.length || 0) === 0 ||
                        (groups[1]?.files?.length || 0) === 0))
                  }
                  buttonType="table"
                />
              </div>
            </Col>
          </Row>

          {/* Results Container */}
          {(tableData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={tableData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={
                    analysisMode === "single"
                      ? selectedFiles.length
                      : (groups[0]?.files?.length || 0) +
                        (groups[1]?.files?.length || 0)
                  }
                  title="RRTS Analysis Results"
                  onRetry={handleGenerate}
                  showFileCount={true}
                  isTable={true}
                  showDownloadOptions={true}
                />
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RRTS;
